import { useState, useEffect } from 'react';
import Card from './Card';
import Sparkline from './Sparkline';
import useCountUp from '../hooks/useCountUp';
import {
  TrendingUp,
  TrendingDown,
  Briefcase,
  BadgeCheck,
  AlertTriangle,
  Users,
} from 'lucide-react';
import { TONES } from '../theme';
import { useNavigate } from 'react-router-dom';

const ICONS = {
  briefcase: Briefcase,
  badge: BadgeCheck,
  user: AlertTriangle,
  manage_accounts: Users,
};

// Static design config per API metric key (label / icon / tone / spark color
// stay the same as before — the API only provides values).
// If a label/tone name differs from your old `../data` KPIS, just edit this list.
const METRIC_CONFIG = [
  {
    key: 'pending_applications',
    label: 'Pending Applications',
    icon: 'briefcase',
    tone: 'amber',
    spark: '#F59E0B',
  },
  {
    key: 'certificates_issued_30d',
    label: 'Certificates Issued (30d)',
    icon: 'badge',
    tone: 'emerald',
    spark: '#10B981',
  },
  {
    key: 'verifications',
    label: 'Verifications',
    icon: 'user',
    tone: 'rose',
    spark: '#F43F5E',
  },
  {
    key: 'total_customers',
    label: 'Total Customers',
    icon: 'manage_accounts',
    tone: 'indigo',
    spark: '#6366F1',
  },
];

function StatCard({ kpi, index }) {
  const value = useCountUp(kpi.value, 1000 + index * 120);
  const up = kpi.deltaTone === 'up';
  const down = kpi.deltaTone === 'down';
  const Icon = ICONS[kpi.icon] || Briefcase;

  // Guard: fall back to the first known tone instead of crashing if a
  // config tone name doesn't exist in TONES.
  const tone = TONES[kpi.tone] || TONES[Object.keys(TONES)[0]];
  if (!TONES[kpi.tone]) {
    console.warn(
      `KpiGrid: tone "${kpi.tone}" not found in TONES — ` +
        `valid tones: ${Object.keys(TONES).join(', ')}`,
    );
  }

  return (
    <Card delay={120 + index * 70} className="group p-5 hover:-translate-y-1">
      <div className="flex items-center justify-between">
        <span
          className={`w-10 h-10 rounded-xl ${tone.tile} flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 group-hover:ring-2`}
        >
          <Icon size={18} strokeWidth={1.9} />
        </span>
        {(up || down) && (
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-md px-1.5 py-0.5 tabular-nums ${
              up ? 'text-[#047857] bg-[#ECFDF5]' : 'text-[#BE123C] bg-[#FFF1F2]'
            }`}
          >
            {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {kpi.delta}
          </span>
        )}
      </div>

      <p className="mt-4 text-[28px] leading-9 font-bold text-slate-900 tabular-nums tracking-tight">
        {value}
      </p>
      <p className="text-xs font-medium text-slate-500 mt-0.5">{kpi.label}</p>

      <div className="mt-4 transition-transform duration-300 group-hover:scale-[1.04] origin-bottom">
        <Sparkline points={kpi.sparkData} id={index} color={kpi.spark} />
      </div>

      <p className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-400">
        {kpi.caption}
      </p>
    </Card>
  );
}

// Maps the dashboard/metrics API response onto the card shape StatCard expects.
function mapMetricsToKpis(metrics) {
  return METRIC_CONFIG.map((cfg) => {
    const m = (metrics && metrics[cfg.key]) || {};
    const change = m.change_percent;

    return {
      ...cfg,
      value: typeof m.value === 'number' ? m.value : 0,
      delta: change == null ? '' : `${Math.abs(Number(change)).toFixed(1)}%`,
      deltaTone: change == null ? 'flat' : change >= 0 ? 'up' : 'down',
      caption: m.caption || '',
      sparkData: Array.isArray(m.sparkline) ? m.sparkline : [],
    };
  });
}

export default function KpiGrid() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [metrics, setMetrics] = useState(null);
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const getMetrics = async () => {
    try {
      const response = await GetAPIcalls('/api/v1/agency/dashboard/metrics');
      if (response.status == 200) {
        setMetrics(response.data);
      }
    } catch (err) {
      setMetrics(null);
      // showToast(err.message, 'error');
      console.error(err.message);
    }
  };

  useEffect(() => {
    getMetrics();
  }, []);

  const kpis = mapMetricsToKpis(metrics);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
      {kpis.map((kpi, i) => (
        <StatCard key={kpi.key} kpi={kpi} index={i} />
      ))}
    </div>
  );
}
